import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { PricingTable, useUser, SignedIn, SignedOut, RedirectToSignIn } from '@clerk/clerk-react';
import './SignupPage.css';

const SignupPage = () => {
  const { user, isLoaded } = useUser();
  const [showDevMode, setShowDevMode] = useState(false);
  const [forceProductionMode, setForceProductionMode] = useState(false);

  useEffect(() => {
    // Update page title for signup page
    document.title = "Get Started - StudioIQ";
    window.scrollTo(0, 0);
    
    // Check URL parameters for forcing development mode
    const urlParams = new URLSearchParams(window.location.search);
    const devMode = urlParams.get('dev');
    const testMode = urlParams.get('test');
    
    if (testMode === 'production') {
      setForceProductionMode(true);
      return;
    }
    
    // Only show dev mode when explicitly requested with ?dev=true
    if (devMode === 'true') {
      setShowDevMode(true);
      
      // Initialize billing toggle functionality for development preview
      const handleDevBillingToggle = (event) => {
        const isAnnual = event.target.checked;
        const monthlyPrices = document.querySelectorAll('.monthly-price');
        const annualPrices = document.querySelectorAll('.annual-price');
        
        if (isAnnual) {
          monthlyPrices.forEach(price => price.style.display = 'none');
          annualPrices.forEach(price => price.style.display = 'block');
        } else {
          monthlyPrices.forEach(price => price.style.display = 'block');
          annualPrices.forEach(price => price.style.display = 'none');
        }
      };

      setTimeout(() => {
        const toggle = document.getElementById('dev-billing-toggle');
        if (toggle) {
          toggle.addEventListener('change', handleDevBillingToggle);
          
          // Set initial state to show annual prices (since toggle defaults to checked)
          const monthlyPrices = document.querySelectorAll('.monthly-price');
          const annualPrices = document.querySelectorAll('.annual-price');
          
          monthlyPrices.forEach(price => price.style.display = 'none');
          annualPrices.forEach(price => price.style.display = 'block');
        }
      }, 100);
    }
  }, [forceProductionMode]);

  // Show loading while Clerk is loading (only in production environments without localhost)
  if (!isLoaded && !showDevMode && !window.location.hostname.includes('localhost')) {
    return (
      <div className="signup-container">
        <header className="signup-header">
          <div className="header-content">
            <Link to="/" className="logo">StudioIQ</Link>
            <Link to="/" className="back-link">← Back to Home</Link>
          </div>
        </header>
        <div className="signup-main">
          <div className="signup-content">
            <div className="signup-intro">
              <h1>Loading...</h1>
              <p>Please wait while we prepare your signup experience.</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show development mode only on localhost (unless forced to production mode)
  if (showDevMode && !forceProductionMode) {
    return (
      <div className="signup-container">
        <header className="signup-header">
          <div className="header-content">
            <Link to="/" className="logo">StudioIQ</Link>
            <Link to="/" className="back-link">← Back to Home</Link>
          </div>
        </header>

        <div className="signup-main">
          <div className="signup-content">
            <div className="signup-intro">
              <h1>Clerk Billing Integration - Development Mode</h1>
              <p>🚧 <strong>Development Environment Detected</strong></p>
              <p>Clerk Billing with PricingTable component is configured but requires production domain (studioiq.co) to function.</p>
            </div>
            
            <div className="development-info">
              <h3>✅ Implementation Status:</h3>
              <ul>
                <li>✅ Clerk PricingTable component integrated</li>
                <li>✅ Custom styling applied for luxury aesthetic</li>
                <li>✅ SignedIn/SignedOut flow implemented</li>
                <li>✅ Production keys configured</li>
                <li>✅ All plan IDs configured (annual discount handled by Clerk)</li>
              </ul>
              
              <h3>💰 Pricing Preview:</h3>
              <div className="pricing-preview">
                <div className="billing-toggle">
                  <span className="toggle-label">Monthly</span>
                  <label className="toggle-switch">
                    <input type="checkbox" id="dev-billing-toggle" defaultChecked />
                    <span className="slider"></span>
                  </label>
                  <span className="toggle-label">Annual <span className="save-badge">Save 20%</span></span>
                </div>
                
                <div className="dev-plan-grid">
                  <div className="dev-plan-card">
                    <div className="plan-badge">Recommended</div>
                    <div className="plan-name">Starter</div>
                    <div className="plan-price-display">
                      <div className="plan-price monthly-price" style={{display: 'none'}}>$47/month</div>
                      <div className="plan-price annual-price" style={{display: 'block'}}>$39/month</div>
                      <div className="plan-annual annual-price" style={{display: 'block'}}>Billed annually ($468/year)</div>
                      <div className="plan-annual monthly-price" style={{display: 'none'}}>Billed monthly</div>
                    </div>
                    <div className="plan-description">Perfect for independent jewelry stores</div>
                  </div>
                  
                  <div className="dev-plan-card">
                    <div className="plan-badge">Most Popular</div>
                    <div className="plan-name">Pro</div>
                    <div className="plan-price-display">
                      <div className="plan-price monthly-price" style={{display: 'none'}}>$95/month</div>
                      <div className="plan-price annual-price" style={{display: 'block'}}>$79/month</div>
                      <div className="plan-annual annual-price" style={{display: 'block'}}>Billed annually ($948/year)</div>
                      <div className="plan-annual monthly-price" style={{display: 'none'}}>Billed monthly</div>
                    </div>
                    <div className="plan-description">Growing jewelry businesses</div>
                  </div>
                  
                  <div className="dev-plan-card">
                    <div className="plan-name">Enterprise</div>
                    <div className="plan-price-display">
                      <div className="plan-price monthly-price" style={{display: 'none'}}>$240/month</div>
                      <div className="plan-price annual-price" style={{display: 'block'}}>$199/month</div>
                      <div className="plan-annual annual-price" style={{display: 'block'}}>Billed annually ($2,388/year)</div>
                      <div className="plan-annual monthly-price" style={{display: 'none'}}>Billed monthly</div>
                    </div>
                    <div className="plan-description">Multi-location businesses</div>
                  </div>
                </div>
              </div>

              <h3>🔧 Ready for Next Phase:</h3>
              <ol>
                <li>✅ Frontend PricingTable integration complete</li>
                <li>⏳ Phase 2B: Backend migration (remove Stripe, add Clerk webhooks)</li>
                <li>⏳ Phase 2C: Feature implementation (usage limits, admin roles)</li>
                <li>🧪 Test PricingTable on production domain (studioiq.co)</li>
              </ol>

              <h3>🧪 Access Development Mode:</h3>
              <p>To access the development preview when needed:</p>
              <Link to="/signup?dev=true" className="btn-secondary">Development Mode</Link>
            </div>
            
            <div className="demo-notice">
              <p><strong>Note:</strong> This page will automatically show the Clerk PricingTable when accessed from studioiq.co domain.</p>
              <Link to="/" className="btn-secondary">← Back to Landing Page</Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Production version with Clerk PricingTable (will work on studioiq.co)
  return (
    <div className="signup-container">
      <header className="signup-header">
        <div className="header-content">
          <Link to="/" className="logo">StudioIQ</Link>
          <Link to="/" className="back-link">← Back to Home</Link>
        </div>
      </header>

      <SignedOut>
        <div className="signup-main">
          <div className="signup-content">
            <div className="signup-intro">
              <h1>Get Started with StudioIQ</h1>
              <p>Join hundreds of jewelry professionals who use StudioIQ to generate accurate repair quotes, protect their margins, and grow their business.</p>
              <p>Please sign in to continue with your subscription.</p>
            </div>
            <div className="clerk-signin-container">
              <RedirectToSignIn 
                redirectUrl={process.env.REACT_APP_CLERK_SIGN_IN_FORCE_REDIRECT_URL}
                signUpUrl={process.env.REACT_APP_CLERK_SIGN_UP_FORCE_REDIRECT_URL}
              />
            </div>
          </div>
        </div>
      </SignedOut>

      <SignedIn>
        <div className="signup-main">
          <div className="signup-content">
            <div className="signup-intro">
              <h1>Choose Your Plan</h1>
              <p>Welcome {user?.firstName}! Select the perfect plan for your jewelry business.</p>
            </div>
            
            <div className="pricing-container">
              <PricingTable 
                appearance={{
                  elements: {
                    pricingTable: "clerk-pricing-table",
                    pricingTableCard: "clerk-pricing-card",
                    pricingTableCardSelected: "clerk-pricing-card-selected",
                    pricingTableCardFeatures: "clerk-pricing-features",
                    pricingTableCardTitle: "clerk-pricing-title",
                    pricingTableCardPrice: "clerk-pricing-price",
                    pricingTableCardDescription: "clerk-pricing-description",
                    pricingTableCardAction: "clerk-pricing-action"
                  },
                  variables: {
                    colorPrimary: "#1a1a1a",
                    colorText: "#1a1a1a",
                    colorBackground: "#ffffff",
                    borderRadius: "12px"
                  }
                }}
                newSubscriptionRedirectUrl="https://studioiq.app/"
              />
            </div>

            <div className="trial-info">
              <p><strong>🔐 Secure Payment Processing</strong></p>
              <p>Start today • Cancel anytime • No contracts</p>
            </div>
            
            <div className="terms-note">
              By subscribing, you agree to our <Link to="/terms-of-service">Terms of Service</Link> and <Link to="/privacy-policy">Privacy Policy</Link>
            </div>
          </div>
        </div>
      </SignedIn>
    </div>
  );
};

export default SignupPage;